import React from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, CheckCircle2, Cpu, Wrench, Target, BarChart3, Lightbulb, ArrowRight } from 'lucide-react';
import { ModuleData } from '../data/modulesData';

interface ModuleDetailProps {
  module: ModuleData;
  onBack: () => void;
  onBook: () => void;
  t: (key: string) => string;
}

export function ModuleDetail({ module, onBack, onBook, t }: ModuleDetailProps) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="min-h-screen bg-black text-white p-6 md:p-12 lg:p-24"
    >
      <div className="max-w-5xl mx-auto">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors mb-12 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="text-[14px] font-bold uppercase tracking-widest">{t("Back to Programs")}</span>
        </button>

        {/* Header */}
        <header className="mb-16">
          <div className="inline-block px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[12px] font-bold uppercase tracking-[0.2em] mb-6">
            {t(module.ageGroup)}
          </div>
          <h1 className="text-[40px] md:text-[64px] font-bold leading-tight tracking-tight mb-6">
            {t(module.title)}
          </h1>
        </header>

        {/* Section 1: The Vision */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Lightbulb className="w-6 h-6 text-blue-400" />
            <h2 className="text-[24px] font-bold uppercase tracking-widest text-white/80">{t("The Vision")}</h2>
          </div>
          <div className="bg-titanium border border-silver rounded-2xl p-8 md:p-12">
            <p className="text-[18px] md:text-[24px] leading-relaxed text-white/90 font-medium">
              {t(module.narrativeHook)}
            </p>
          </div>
        </section>

        {/* Section 2: Toolkit & Ecosystem */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Wrench className="w-6 h-6 text-blue-400" />
            <h2 className="text-[24px] font-bold uppercase tracking-widest text-white/80">{t("Toolkit & Ecosystem")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Tech Stack */}
            <div className="bg-titanium border border-silver rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-6 text-blue-400">
                <Cpu className="w-5 h-5" />
                <h3 className="text-[16px] font-bold uppercase tracking-widest">{t("Tech Stack")}</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {module.techStack.map((tech, idx) => (
                  <span key={idx} className="px-4 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-[14px] text-blue-100">
                    {t(tech)}
                  </span>
                ))}
              </div>
            </div>
            {/* Hardware */}
            <div className="bg-titanium border border-silver rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-6 text-purple-400">
                <Cpu className="w-5 h-5" />
                <h3 className="text-[16px] font-bold uppercase tracking-widest">{t("Hardware")}</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {module.hardware.map((item, idx) => (
                  <span key={idx} className="px-4 py-2 rounded-lg bg-purple-500/10 border border-purple-500/20 text-[14px] text-purple-100">
                    {t(item)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Student Deliverable */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Target className="w-6 h-6 text-emerald-400" />
            <h2 className="text-[24px] font-bold uppercase tracking-widest text-white/80">{t("Student Deliverable")}</h2>
          </div>
          <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 rounded-2xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="flex items-start gap-4 relative z-10">
              <CheckCircle2 className="w-8 h-8 text-emerald-400 shrink-0 mt-1" />
              <p className="text-[20px] md:text-[24px] leading-relaxed text-emerald-50 font-medium">
                {t(module.deliverable)}
              </p>
            </div>
          </div>
        </section>

        {/* Section 4: QEF Evaluation Metrics */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <BarChart3 className="w-6 h-6 text-blue-400" />
            <h2 className="text-[24px] font-bold uppercase tracking-widest text-white/80">{t("QEF Evaluation Metrics")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-silver rounded-2xl overflow-hidden">
            <div className="bg-titanium p-8 md:p-10">
              <h3 className="text-[14px] font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">{t("Quantitative")}</h3>
              <p className="text-[16px] leading-relaxed text-white/70">
                {t(module.qefQuantitative)}
              </p>
            </div>
            <div className="bg-titanium p-8 md:p-10">
              <h3 className="text-[14px] font-bold uppercase tracking-[0.2em] text-purple-400 mb-4">{t("Qualitative")}</h3>
              <p className="text-[16px] leading-relaxed text-white/70">
                {t(module.qefQualitative)}
              </p>
            </div>
          </div>
        </section>

        {/* CTA Button */}
        <div className="mt-16 flex justify-center pb-24">
          <motion.button 
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBook}
            className="relative group overflow-hidden rounded-full"
          >
            <div className="absolute inset-0 bg-blue-600 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            <div className="relative border border-white/20 px-12 py-6 rounded-full flex items-center gap-4 transition-colors group-hover:border-transparent group-hover:text-white bg-blue-600/20 backdrop-blur-sm">
              <span className="text-[18px] font-bold uppercase tracking-widest text-blue-400 group-hover:text-white">{t("Book This Module")}</span>
              <ArrowRight className="w-6 h-6 text-blue-400 group-hover:text-white group-hover:translate-x-2 transition-all duration-500" />
            </div>
          </motion.button>
        </div>

      </div>
    </motion.div>
  );
}
